import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, ArrowLeftRight, ArrowRight, Box, Briefcase, Calculator, CheckCircle2, ChevronRight,
  Coins, FileText, Globe2, Headphones, Loader2, MapPin, Package, PackageCheck, Plus, RotateCcw,
  Search, Shirt, ShoppingBag, Trash2, Truck, UtensilsCrossed, X,
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ShopStrip from "../components/home/ShopStrip";
import { usePageMeta } from "../components/service/ServiceBits";
import api, { apiError } from "../lib/api";

const SLOTS = ["10 AM – 1 PM", "1 PM – 4 PM", "4 PM – 7 PM"];
const SHIP_TYPES = ["Document", "Parcel", "Food", "Clothing", "Commercial", "eCommerce", "Personal Items", "Other"];
const CURRENCIES = ["INR", "USD", "GBP", "EUR", "AUD", "CAD", "AED", "NZD", "SGD"];
const COUNTRIES = ["USA", "Canada", "UK", "UAE", "Australia", "New Zealand", "Singapore", "Germany", "France", "Saudi Arabia"];

const input = "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-navy outline-none transition-colors placeholder:text-slate-400 focus:border-saffron focus:ring-2 focus:ring-saffron/20";
const label = "mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-slate-500";

const TRUST = [
  [Package, "Free Pickup"], [MapPin, "30,000+ PIN Codes"], [Globe2, "200+ Countries"],
  [Truck, "Multiple Courier Partners"], [Search, "Shipment Tracking"], [Headphones, "24/7 Customer Support"],
];

const TYPE_CARDS = [
  { icon: FileText, label: "Documents", type: "Document", desc: "Papers, certificates, contracts" },
  { icon: Package, label: "Personal Parcel", type: "Parcel", desc: "Everyday packages & gifts" },
  { icon: UtensilsCrossed, label: "Food / NRI", type: "Food", desc: "Permitted food items" },
  { icon: Briefcase, label: "Business", type: "Commercial", desc: "Commercial & bulk shipping" },
  { icon: ShoppingBag, label: "eCommerce", type: "eCommerce", desc: "Online seller orders" },
  { icon: Globe2, label: "International", type: "Parcel", desc: "Worldwide destinations", intl: true },
];

const PACK_CARDS = [
  { icon: FileText, label: "Document Envelope" }, { icon: Package, label: "Parcel Box" },
  { icon: UtensilsCrossed, label: "Food Package" }, { icon: Shirt, label: "Clothing Package" },
  { icon: Box, label: "Fragile Package" },
];

const JOURNEY = [
  { icon: FileText, title: "Pickup Request" }, { icon: Truck, title: "Pickup Executive" },
  { icon: Box, title: "Processing" }, { icon: PackageCheck, title: "Courier Partner" },
  { icon: Globe2, title: "Transit" }, { icon: MapPin, title: "Destination" }, { icon: CheckCircle2, title: "Delivery" },
];

const STEPS = ["Origin", "Destination", "Shipment", "Details", "Review"];
const emptyBox = () => ({ l: "", w: "", h: "", wt: "", qty: 1 });

export default function PickupRequest() {
  const [contact, setContact] = useState(null);
  const [config, setConfig] = useState({ volumetric_divisor_domestic: 5000, volumetric_divisor_international: 5000, weight_rounding_kg: 1 });
  const [forex, setForex] = useState(null);
  const [hasShop, setHasShop] = useState(true);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({
    pincode: "", city: "", state: "", address: "", address2: "",
    is_international: false, dest_pincode: "", dest_city: "", dest_country: "",
    package_type: "Parcel", shipment_desc: "", actual_weight: "", pieces: 1,
    name: "", phone: "", email: "", pickup_date: "", time_slot: SLOTS[0], notes: "",
  });
  const [boxes, setBoxes] = useState([emptyBox()]);
  const [unit, setUnit] = useState("cm");
  const [calcType, setCalcType] = useState("domestic");
  const [cur, setCur] = useState({ amount: "100", from: "USD", to: "INR" });
  const formRef = useRef(null);
  const calcRef = useRef(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const phone = searchParams.get("phone");
    const pincode = searchParams.get("pincode");
    const date = searchParams.get("date");
    const destination = searchParams.get("destination");
    if (phone || pincode || date || destination) {
      setForm((f) => ({
        ...f,
        ...(phone ? { phone } : {}),
        ...(pincode ? { pincode } : {}),
        ...(date ? { pickup_date: date } : {}),
        ...(destination ? { dest_city: destination } : {}),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  usePageMeta(
    "Schedule a Courier Pickup | IndiaFast Global Couriers",
    "Request a domestic or international courier pickup with IndiaFast Global Couriers. Calculate volumetric weight, convert currencies and explore IndiaFast Bazaar.",
    "/pickup-request"
  );

  useEffect(() => {
    api.get("/public/home").then((r) => setContact(r.data.contact)).catch(() => {});
    api.get("/public/calculator-config").then((r) => setConfig(r.data)).catch(() => {});
    api.get("/public/forex").then((r) => setForex(r.data)).catch(() => {});
    api.get("/shop/public/home").then((r) => setHasShop(!!(r.data.categories || []).length)).catch(() => setHasShop(false));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const today = new Date().toISOString().slice(0, 10);
  const scrollTo = (ref) => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const lookupPin = async (pin, target) => {
    if (!/^\d{6}$/.test(pin)) return;
    try {
      const r = await api.get(`/public/pincode/${pin}`);
      if (r.data?.city) setForm((f) => ({ ...f, [target === "from" ? "city" : "dest_city"]: f[target === "from" ? "city" : "dest_city"] || r.data.city,
        ...(target === "from" && r.data.state && !f.state ? { state: r.data.state } : {}) }));
    } catch {}
  };

  // ---- volumetric calculator ----
  const divisor = calcType === "domestic" ? config.volumetric_divisor_domestic : config.volumetric_divisor_international;
  const roundKg = Math.max(0.5, config.weight_rounding_kg || 1);
  const calc = useMemo(() => {
    let actual = 0, vol = 0;
    const perBox = boxes.map((b) => {
      const factor = unit === "inch" ? 2.54 : 1;
      const l = (parseFloat(b.l) || 0) * factor, w = (parseFloat(b.w) || 0) * factor, h = (parseFloat(b.h) || 0) * factor;
      const qty = Math.max(1, parseInt(b.qty) || 1);
      const bw = (parseFloat(b.wt) || 0) * qty;
      const bv = ((l * w * h) / (divisor || 5000)) * qty;
      actual += bw; vol += bv;
      return { ...b, vol: bv, act: bw, charge: Math.max(bw, bv) };
    });
    const chargeable = Math.ceil(Math.max(actual, vol) / roundKg) * roundKg;
    return { actual, vol, chargeable: Math.max(actual, vol) > 0 ? chargeable : 0, perBox };
  }, [boxes, unit, divisor, roundKg]);

  const useCalcWeight = () => {
    if (calc.vol <= 0 && calc.actual <= 0) { toast.error("Enter package dimensions or weight first"); return; }
    setForm((f) => ({ ...f, volumetric_weight: Number(calc.vol.toFixed(2)),
      actual_weight: calc.actual ? Number(calc.actual.toFixed(2)) : f.actual_weight,
      pieces: calc.perBox.reduce((s, b) => s + (parseInt(b.qty) || 1), 0) }));
    toast.success(`Chargeable weight ${calc.chargeable} kg attached to your request`);
    scrollTo(formRef);
  };

  // ---- currency ----
  const converted = useMemo(() => {
    if (!forex?.rates) return null;
    const amt = parseFloat(cur.amount) || 0;
    const rFrom = forex.rates[cur.from] || 1, rTo = forex.rates[cur.to] || 1;
    return (amt / rFrom) * rTo;
  }, [cur, forex]);
  const unitRate = useMemo(() => {
    if (!forex?.rates) return null;
    return (forex.rates[cur.to] || 1) / (forex.rates[cur.from] || 1);
  }, [cur.from, cur.to, forex]);

  // ---- wizard ----
  const stepValid = (s) => {
    if (s === 1) return /^\d{6}$/.test(form.pincode) && form.city.trim() && form.address.trim();
    if (s === 2) return form.is_international ? !!form.dest_country.trim() : /^\d{6}$/.test(form.dest_pincode);
    if (s === 3) return !!form.package_type;
    if (s === 4) return form.name.trim() && form.phone.replace(/\D/g, "").length >= 10 && form.pickup_date && form.time_slot;
    return true;
  };
  const next = () => { if (!stepValid(step)) { toast.error("Please complete the required fields"); return; } setStep((s) => Math.min(5, s + 1)); };

  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.post("/public/pickup-requests", {
        name: form.name, phone: form.phone, email: form.email,
        address: form.address, address2: form.address2, pincode: form.pincode,
        city: form.city, state: form.state, pickup_date: form.pickup_date, time_slot: form.time_slot,
        package_type: form.package_type, pieces: parseInt(form.pieces) || 1,
        actual_weight: parseFloat(form.actual_weight) || 0, volumetric_weight: form.volumetric_weight || 0,
        notes: form.notes, is_international: form.is_international,
        dest_pincode: form.dest_pincode, dest_city: form.dest_city,
        dest_country: form.is_international ? form.dest_country : "India",
        shipment_desc: form.shipment_desc,
      });
      setDone(res.data.request_id);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const pickType = (t) => {
    setForm((f) => ({ ...f, package_type: t.type, is_international: !!t.intl }));
    setStep(3);
    scrollTo(formRef);
  };

  const waNum = (contact?.phone || "").replace(/\D/g, "").slice(-10);

  const summaryRows = [
    ["Pickup Location", `${form.address}${form.address2 ? `, ${form.address2}` : ""}, ${form.city} ${form.pincode}`],
    ["Destination", form.is_international ? `${form.dest_country} (International)` : `${form.dest_city ? `${form.dest_city} ` : ""}${form.dest_pincode} (Domestic)`],
    ["Shipment Type", form.package_type], ["Packages", `${form.pieces}`],
    ["Weight", `${form.actual_weight || 0} kg actual${form.volumetric_weight ? ` · ${form.volumetric_weight} kg volumetric` : ""}`],
    ["Pickup", `${form.pickup_date} · ${form.time_slot}`],
    ["Customer", `${form.name} · +91 ${form.phone.replace(/\D/g, "").slice(-10)}`],
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="pickup-request-page">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-navy pb-20 pt-32 sm:pt-36" data-testid="pickup-hero">
        <div className="pointer-events-none absolute -right-24 top-0 h-80 w-80 rounded-full bg-saffron/15 blur-3xl" />
        <div className="tricolor-beam absolute inset-x-0 top-0 h-1.5" />
        <svg className="pointer-events-none absolute inset-0 h-full w-full opacity-20" aria-hidden="true">
          <line x1="8%" y1="65%" x2="30%" y2="40%" stroke="#f97316" strokeWidth="1" strokeDasharray="4 6" className="network-dash" />
          <line x1="30%" y1="40%" x2="55%" y2="60%" stroke="#ffffff" strokeWidth="1" strokeDasharray="4 6" className="network-dash" />
          <line x1="55%" y1="60%" x2="80%" y2="30%" stroke="#16a34a" strokeWidth="1" strokeDasharray="4 6" className="network-dash" />
          <circle cx="8%" cy="65%" r="4" fill="#f97316" className="network-pulse" />
          <circle cx="30%" cy="40%" r="4" fill="#ffffff" className="network-pulse" style={{ animationDelay: "0.6s" }} />
          <circle cx="55%" cy="60%" r="4" fill="#f97316" className="network-pulse" style={{ animationDelay: "1.2s" }} />
          <circle cx="80%" cy="30%" r="4" fill="#16a34a" className="network-pulse" style={{ animationDelay: "1.8s" }} />
        </svg>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
          <p className="animate-fade-up flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-saffron">
            <Truck className="h-4 w-4" /> Doorstep Pickup
          </p>
          <h1 className="animate-fade-up stagger-1 mt-3 max-w-3xl font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
            Schedule Your <span className="text-saffron">Pickup</span>
          </h1>
          <p className="animate-fade-up stagger-2 mt-4 max-w-2xl text-sm leading-relaxed text-slate-300 sm:text-base">
            Tell us what you're shipping and where it's going. IndiaFast will take care of the pickup and delivery journey.
          </p>
          <div className="animate-fade-up stagger-3 mt-7 flex flex-col gap-3 sm:flex-row">
            <button onClick={() => scrollTo(formRef)} data-testid="pickup-hero-book"
                    className="group flex items-center justify-center gap-2 rounded-full bg-saffron px-8 py-3.5 text-sm font-bold text-white shadow-xl shadow-saffron/30 transition-all hover:-translate-y-0.5 hover:bg-saffron-hover">
              Book a Pickup <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button onClick={() => scrollTo(calcRef)} data-testid="pickup-hero-calc"
                    className="flex items-center justify-center gap-2 rounded-full border border-white/25 bg-white/10 px-8 py-3.5 text-sm font-bold text-white backdrop-blur transition-colors hover:bg-white/20">
              <Calculator className="h-4 w-4" /> Calculate Shipping
            </button>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="border-b border-slate-200 bg-white" data-testid="pickup-trust">
        <div className="mx-auto grid max-w-7xl grid-cols-2 px-4 sm:grid-cols-3 sm:px-6 lg:grid-cols-6 lg:divide-x lg:divide-slate-100">
          {TRUST.map(([Icon, t], i) => (
            <div key={i} className="flex items-center justify-center gap-2 px-3 py-4" data-testid={`pickup-trust-${i}`}>
              <Icon className="h-4 w-4 shrink-0 text-saffron" />
              <span className="text-[11px] font-bold text-navy sm:text-xs">{t}</span>
            </div>
          ))}
        </div>
      </section>

      <main className="mx-auto max-w-7xl px-4 sm:px-6">
        {done ? (
          /* SUCCESS SCREEN */
          <div className="mx-auto max-w-xl py-14" data-testid="pkr-success">
            <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white text-center shadow-xl">
              <div className="tricolor-beam h-1.5" />
              <div className="p-8 sm:p-10">
                <span className="mx-auto grid h-20 w-20 animate-bounce place-items-center rounded-full bg-emerald-600/10">
                  <PackageCheck className="h-10 w-10 text-emerald-600" />
                </span>
                <h2 className="mt-5 font-heading text-2xl font-extrabold text-navy sm:text-3xl">Your Pickup Request Has Been Received</h2>
                <p className="mt-2 text-sm text-slate-500">Our team will review the request and contact you to confirm the pickup.</p>
                <p className="mt-4 font-mono text-3xl font-extrabold text-express" data-testid="pkr-request-id">{done}</p>
                <div className="mt-6 space-y-2 rounded-2xl bg-slate-50 p-4 text-left">
                  {[["Pickup Date", form.pickup_date], ["Time Slot", form.time_slot],
                    ["Pickup From", `${form.city} ${form.pincode}`],
                    ["Destination", form.is_international ? form.dest_country : form.dest_pincode],
                    ["Mobile", `+91 ${form.phone.replace(/\D/g, "").slice(-10)}`]].map(([k, v]) => (
                    <p key={k} className="flex justify-between gap-4 text-xs"><span className="font-bold uppercase tracking-wider text-slate-400">{k}</span><span className="font-semibold text-navy">{v}</span></p>
                  ))}
                </div>
                <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
                  <Link to="/track" data-testid="pkr-success-track" className="rounded-full bg-saffron px-6 py-3 text-sm font-bold text-white shadow-lg shadow-saffron/30 hover:bg-saffron-hover">Track Shipment</Link>
                  {waNum && <a href={`https://wa.me/91${waNum}?text=${encodeURIComponent(`Hi IndiaFast, my pickup request ID is ${done}`)}`} target="_blank" rel="noreferrer" data-testid="pkr-success-whatsapp"
                               className="rounded-full bg-emerald-600 px-6 py-3 text-sm font-bold text-white hover:bg-emerald-500">Talk on WhatsApp</a>}
                  <Link to="/" data-testid="pkr-success-home" className="rounded-full border border-slate-300 px-6 py-3 text-sm font-bold text-navy hover:bg-slate-100">Back to Home</Link>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* BOOKING WIZARD */}
            <section ref={formRef} className="scroll-mt-24 py-14" data-testid="pickup-booking">
              <div className="grid items-start gap-8 lg:grid-cols-5">
                {/* Left visual */}
                <div className="order-2 hidden lg:order-1 lg:col-span-2 lg:block">
                  <div className="sticky top-24 overflow-hidden rounded-3xl bg-navy p-7 shadow-xl" data-testid="pickup-visual">
                    <div className="tricolor-beam absolute inset-x-0 top-0 h-1" />
                    <p className="text-xs font-bold uppercase tracking-[0.25em] text-saffron">How pickup works</p>
                    <h3 className="mt-2 font-heading text-xl font-extrabold text-white">We come to your door. You relax.</h3>
                    <div className="mt-6 space-y-4">
                      {[
                        [Package, "Package ready", "Pack it, or ask us for packing help"],
                        [Truck, "Executive arrives", "At your chosen date & time slot"],
                        [PackageCheck, "Weighed & labelled", "Chargeable weight confirmed"],
                        [Globe2, "On its way", "Track every step online"],
                      ].map(([Icon, t, d], i) => (
                        <div key={i} className="flex gap-3.5">
                          <span className="relative grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white/10 text-saffron">
                            <Icon className="h-5 w-5" />
                            {i < 3 && <span className="absolute left-1/2 top-10 h-4 w-px bg-saffron/40" />}
                          </span>
                          <div>
                            <p className="text-sm font-bold text-white">{t}</p>
                            <p className="text-xs text-slate-400">{d}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-7 rounded-2xl border border-white/10 bg-white/5 p-4">
                      <p className="text-xs font-bold text-white">Need help right now?</p>
                      {waNum && (
                        <a href={`https://wa.me/91${waNum}`} target="_blank" rel="noreferrer" data-testid="pickup-visual-whatsapp"
                           className="mt-2 inline-flex items-center gap-2 text-xs font-bold text-emerald-400 hover:text-emerald-300">
                          <Headphones className="h-3.5 w-3.5" /> Chat with our team on WhatsApp
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {/* Wizard */}
                <div className="order-1 lg:order-2 lg:col-span-3">
                  <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl" data-testid="pkr-form">
                    <div className="tricolor-beam h-1.5" />
                    {/* progress */}
                    <div className="border-b border-slate-100 px-6 pb-5 pt-6 sm:px-8">
                      <div className="flex items-center justify-between">
                        {STEPS.map((s, i) => (
                          <div key={s} className="flex flex-1 items-center" data-testid={`pkr-step-dot-${i + 1}`}>
                            <div className="flex flex-col items-center">
                              <span className={`grid h-8 w-8 place-items-center rounded-full text-[11px] font-extrabold transition-colors ${step > i + 1 ? "bg-emerald-600 text-white" : step === i + 1 ? "bg-saffron text-white" : "bg-slate-100 text-slate-400"}`}>
                                {step > i + 1 ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                              </span>
                              <span className={`mt-1 hidden text-[9px] font-bold uppercase tracking-wider sm:block ${step === i + 1 ? "text-saffron" : "text-slate-400"}`}>{s}</span>
                            </div>
                            {i < STEPS.length - 1 && <span className={`mx-1 mb-4 h-0.5 flex-1 rounded ${step > i + 1 ? "bg-emerald-500" : "bg-slate-100"}`} />}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="p-6 sm:p-8">
                      {step === 1 && (
                        <div data-testid="pkr-step-1">
                          <h2 className="font-heading text-lg font-extrabold text-navy">Where are you sending from?</h2>
                          <div className="mt-5 grid gap-4 sm:grid-cols-2">
                            <div><label className={label}>From Pincode *</label>
                              <input required inputMode="numeric" className={input} data-testid="pkr-pincode" placeholder="6 digits"
                                     value={form.pincode} onChange={(e) => { const v = e.target.value.replace(/\D/g, "").slice(0, 6); setForm((f) => ({ ...f, pincode: v })); if (v.length === 6) lookupPin(v, "from"); }} /></div>
                            <div><label className={label}>City / Location *</label><input required className={input} data-testid="pkr-city" value={form.city} onChange={set("city")} /></div>
                            <div className="sm:col-span-2"><label className={label}>Pickup Address *</label>
                              <input required className={input} data-testid="pkr-address" placeholder="Flat / street / landmark" value={form.address} onChange={set("address")} /></div>
                            <div className="sm:col-span-2"><input className={input} data-testid="pkr-address2" placeholder="Address line 2 (optional)" value={form.address2} onChange={set("address2")} /></div>
                            <div><label className={label}>State</label><input className={input} data-testid="pkr-state" value={form.state} onChange={set("state")} /></div>
                          </div>
                        </div>
                      )}

                      {step === 2 && (
                        <div data-testid="pkr-step-2">
                          <h2 className="font-heading text-lg font-extrabold text-navy">Where is your shipment going?</h2>
                          <div className="mt-5 grid grid-cols-2 gap-3" data-testid="pkr-mode-toggle">
                            {[["Domestic", false, MapPin], ["International", true, Globe2]].map(([lbl, intl, Icon]) => (
                              <button key={lbl} type="button" onClick={() => setForm((f) => ({ ...f, is_international: intl }))}
                                      data-testid={`pkr-mode-${lbl.toLowerCase()}`}
                                      className={`flex items-center justify-center gap-2 rounded-xl border-2 py-3.5 text-sm font-bold transition-colors ${form.is_international === intl ? "border-saffron bg-saffron/10 text-saffron" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
                                <Icon className="h-4 w-4" /> {lbl}
                              </button>
                            ))}
                          </div>
                          <div className="mt-5 grid gap-4 sm:grid-cols-2">
                            {form.is_international ? (
                              <div className="sm:col-span-2"><label className={label}>Destination Country *</label>
                                <input required list="pkr-countries" className={input} data-testid="pkr-dest-country" placeholder="e.g. USA" value={form.dest_country} onChange={set("dest_country")} />
                                <datalist id="pkr-countries">{COUNTRIES.map((c) => <option key={c} value={c} />)}</datalist></div>
                            ) : (
                              <>
                                <div><label className={label}>To Pincode *</label>
                                  <input required inputMode="numeric" className={input} data-testid="pkr-dest-pincode" placeholder="6 digits"
                                         value={form.dest_pincode} onChange={(e) => { const v = e.target.value.replace(/\D/g, "").slice(0, 6); setForm((f) => ({ ...f, dest_pincode: v })); if (v.length === 6) lookupPin(v, "to"); }} /></div>
                                <div><label className={label}>City / Location</label><input className={input} data-testid="pkr-dest-city" value={form.dest_city} onChange={set("dest_city")} /></div>
                              </>
                            )}
                          </div>
                        </div>
                      )}

                      {step === 3 && (
                        <div data-testid="pkr-step-3">
                          <h2 className="font-heading text-lg font-extrabold text-navy">What are you shipping?</h2>
                          <div className="mt-5 grid gap-4 sm:grid-cols-2">
                            <div><label className={label}>Shipment Type *</label>
                              <select className={input} data-testid="pkr-package-type" value={form.package_type} onChange={set("package_type")}>
                                {SHIP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                              </select></div>
                            <div><label className={label}>Number of Packages</label>
                              <input type="number" min="1" max="200" className={input} data-testid="pkr-pieces" value={form.pieces} onChange={set("pieces")} /></div>
                            <div><label className={label}>Approximate Weight (kg)</label>
                              <input type="number" step="0.1" min="0" className={input} data-testid="pkr-weight" placeholder="e.g. 2.5" value={form.actual_weight} onChange={set("actual_weight")} /></div>
                            <div className="sm:col-span-2"><label className={label}>Description</label>
                              <textarea rows={2} className={input} data-testid="pkr-notes" placeholder="What's inside? (helps us assign the right courier)" value={form.shipment_desc} onChange={set("shipment_desc")} /></div>
                          </div>
                          <Link to="/p/prohibited" data-testid="pkr-prohibited-link"
                                className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-saffron hover:text-saffron-hover">
                            Check Prohibited / Restricted Items <ChevronRight className="h-3.5 w-3.5" />
                          </Link>
                          {form.volumetric_weight > 0 && (
                            <p className="mt-3 rounded-lg bg-saffron/10 px-3 py-2 text-xs font-semibold text-saffron" data-testid="pkr-vol-attached">
                              Calculator attached: {form.volumetric_weight} kg volumetric — chargeable uses the higher of actual vs volumetric.
                            </p>
                          )}
                        </div>
                      )}

                      {step === 4 && (
                        <div data-testid="pkr-step-4">
                          <h2 className="font-heading text-lg font-extrabold text-navy">Pickup Details</h2>
                          <div className="mt-5 grid gap-4 sm:grid-cols-2">
                            <div><label className={label}>Full Name *</label><input required className={input} data-testid="pkr-name" value={form.name} onChange={set("name")} /></div>
                            <div><label className={label}>Mobile Number *</label><input required inputMode="tel" className={input} data-testid="pkr-phone" placeholder="10-digit mobile" value={form.phone} onChange={set("phone")} /></div>
                            <div className="sm:col-span-2"><label className={label}>Email</label><input type="email" className={input} data-testid="pkr-email" value={form.email} onChange={set("email")} /></div>
                            <div><label className={label}>Preferred Pickup Date *</label><input required type="date" min={today} className={input} data-testid="pkr-date" value={form.pickup_date} onChange={set("pickup_date")} /></div>
                            <div><label className={label}>Preferred Time Slot *</label>
                              <select className={input} data-testid="pkr-slot" value={form.time_slot} onChange={set("time_slot")}>
                                {SLOTS.map((s) => <option key={s} value={s}>{s}</option>)}
                              </select></div>
                          </div>
                        </div>
                      )}

                      {step === 5 && (
                        <div data-testid="pkr-step-5">
                          <h2 className="font-heading text-lg font-extrabold text-navy">Review &amp; Request Pickup</h2>
                          <div className="mt-5 space-y-2.5 rounded-2xl bg-slate-50 p-5" data-testid="pkr-review">
                            {summaryRows.map(([k, v]) => (
                              <p key={k} className="flex justify-between gap-4 border-b border-slate-200/60 pb-2 text-xs last:border-0 last:pb-0">
                                <span className="shrink-0 font-bold uppercase tracking-wider text-slate-400">{k}</span>
                                <span className="text-right font-semibold text-navy">{v}</span>
                              </p>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="mt-8 flex items-center justify-between gap-3">
                        {step > 1 ? (
                          <button type="button" onClick={() => setStep((s) => s - 1)} data-testid="pkr-back-btn"
                                  className="flex items-center gap-1.5 rounded-full border border-slate-300 px-6 py-3 text-sm font-bold text-navy hover:bg-slate-100">
                            <ArrowLeft className="h-4 w-4" /> Back
                          </button>
                        ) : <span />}
                        {step < 5 ? (
                          <button type="button" onClick={next} data-testid="pkr-next-btn"
                                  className="group flex items-center gap-2 rounded-full bg-navy px-8 py-3 text-sm font-bold text-white transition-colors hover:bg-express">
                            Continue <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                          </button>
                        ) : (
                          <button type="button" onClick={submit} disabled={busy} data-testid="pkr-submit-btn"
                                  className="flex items-center gap-2 rounded-full bg-saffron px-8 py-3.5 text-sm font-bold text-white shadow-lg shadow-saffron/30 transition-colors hover:bg-saffron-hover disabled:opacity-60">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PackageCheck className="h-4 w-4" />} REQUEST PICKUP
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* SHIPPING TYPE SELECTOR */}
            <section className="py-12" data-testid="pickup-types">
              <h2 className="text-center font-heading text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">What Are You Shipping?</h2>
              <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
                {TYPE_CARDS.map((t, i) => (
                  <button key={t.label} onClick={() => pickType(t)} data-testid={`pickup-type-${i}`}
                          className="group rounded-2xl border border-slate-200 bg-white p-5 text-center shadow-sm transition-all hover:-translate-y-1 hover:border-saffron/40 hover:shadow-xl">
                    <span className="mx-auto grid h-12 w-12 place-items-center rounded-xl bg-saffron/10 text-saffron transition-colors group-hover:bg-saffron group-hover:text-white"><t.icon className="h-6 w-6" /></span>
                    <p className="mt-3 font-heading text-sm font-bold text-navy">{t.label}</p>
                    <p className="mt-0.5 text-[11px] text-slate-500">{t.desc}</p>
                  </button>
                ))}
              </div>
            </section>

            {/* CALCULATORS */}
            <section ref={calcRef} className="scroll-mt-24 py-12" data-testid="pickup-calculators">
              <h2 className="text-center font-heading text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">Shipping Calculator</h2>
              <p className="mx-auto mt-2 max-w-xl text-center text-sm text-slate-500">Know your chargeable weight and plan international costs before booking.</p>
              <div className="mt-10 grid gap-6 lg:grid-cols-2">
                {/* Volumetric */}
                <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-lg" data-testid="volcalc-card">
                  <div className="border-b border-slate-100 bg-navy p-5">
                    <h3 className="flex items-center gap-2 font-heading text-base font-bold text-white"><Box className="h-5 w-5 text-saffron" /> Calculate Your Volumetric Weight</h3>
                    <p className="mt-1 text-[11px] text-slate-400">Chargeable weight = higher of actual vs volumetric (L×W×H ÷ {divisor}), rounded up to {roundKg} kg.</p>
                  </div>
                  <div className="p-5 sm:p-6">
                    <div className="flex flex-wrap gap-3">
                      <div className="flex rounded-xl border border-slate-200 p-1">
                        {[["cm", "CM"], ["inch", "INCH"]].map(([k, lbl]) => (
                          <button key={k} type="button" onClick={() => setUnit(k)} data-testid={`volcalc-unit-${k}`}
                                  className={`rounded-lg px-4 py-1.5 text-xs font-bold ${unit === k ? "bg-navy text-white" : "text-slate-500"}`}>{lbl}</button>
                        ))}
                      </div>
                      <div className="flex rounded-xl border border-slate-200 p-1">
                        {[["domestic", "Domestic"], ["international", "International"]].map(([k, lbl]) => (
                          <button key={k} type="button" onClick={() => setCalcType(k)} data-testid={`volcalc-type-${k}`}
                                  className={`rounded-lg px-4 py-1.5 text-xs font-bold ${calcType === k ? "bg-saffron text-white" : "text-slate-500"}`}>{lbl}</button>
                        ))}
                      </div>
                    </div>
                    {boxes.map((b, i) => (
                      <div key={i} className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid={`volcalc-box-${i}`}>
                        <div className="mb-2 flex items-center justify-between">
                          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Package {i + 1}</p>
                          {boxes.length > 1 && (
                            <button type="button" onClick={() => setBoxes(boxes.filter((_, j) => j !== i))} data-testid={`volcalc-remove-${i}`}
                                    className="text-slate-400 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                          {[["l", `L ${unit}`, "volcalc-l"], ["w", `W ${unit}`, "volcalc-w"], ["h", `H ${unit}`, "volcalc-h"]].map(([k, ph, tid]) => (
                            <input key={k} type="number" min="0" className={input} placeholder={ph} data-testid={i === 0 ? tid : `${tid}-${i}`}
                                   value={b[k]} onChange={(e) => setBoxes(boxes.map((x, j) => j === i ? { ...x, [k]: e.target.value } : x))} />
                          ))}
                          <input type="number" min="0" step="0.1" className={input} placeholder="Actual kg" data-testid={i === 0 ? "volcalc-wt" : `volcalc-wt-${i}`}
                                 value={b.wt} onChange={(e) => setBoxes(boxes.map((x, j) => j === i ? { ...x, wt: e.target.value } : x))} />
                          <input type="number" min="1" className={input} placeholder="Qty" data-testid={i === 0 ? "volcalc-pieces" : `volcalc-pieces-${i}`}
                                 value={b.qty} onChange={(e) => setBoxes(boxes.map((x, j) => j === i ? { ...x, qty: e.target.value } : x))} />
                        </div>
                        {calc.perBox[i].vol > 0 && (
                          <p className="mt-2 text-[11px] font-semibold text-slate-500">
                            Vol: {calc.perBox[i].vol.toFixed(2)} kg · Actual: {calc.perBox[i].act.toFixed(2)} kg · Chargeable: <span className="text-saffron">{calc.perBox[i].charge.toFixed(2)} kg</span>
                          </p>
                        )}
                      </div>
                    ))}
                    <button type="button" onClick={() => setBoxes([...boxes, emptyBox()])} data-testid="volcalc-add-box"
                            className="mt-3 flex items-center gap-1.5 text-xs font-bold text-saffron hover:text-saffron-hover">
                      <Plus className="h-3.5 w-3.5" /> Add Another Package
                    </button>
                    <div className="mt-5 grid grid-cols-3 gap-2 text-center">
                      {[["Total Actual", calc.actual, "text-white"], ["Total Volumetric", calc.vol, "text-white"], ["Chargeable", calc.chargeable, "text-saffron"]].map(([l, v, c], i) => (
                        <div key={l} className={`rounded-xl p-3 ${i === 2 ? "bg-saffron/15 ring-1 ring-saffron/40" : "bg-navy"}`}>
                          <p className={`text-[9px] font-bold uppercase tracking-wider ${i === 2 ? "text-saffron" : "text-slate-400"}`}>{l}</p>
                          <p className={`mt-1 font-heading text-lg font-extrabold sm:text-xl ${c}`} data-testid={i === 2 ? "volcalc-result" : undefined}>
                            {v.toFixed(2)}<span className="text-[10px] font-semibold opacity-60"> kg</span>
                          </p>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 flex gap-2">
                      <button type="button" onClick={useCalcWeight} data-testid="volcalc-use-btn"
                              className="flex-1 rounded-xl bg-saffron py-3 text-xs font-bold text-white shadow-lg shadow-saffron/30 hover:bg-saffron-hover">
                        Use in My Pickup Request
                      </button>
                      <button type="button" onClick={() => setBoxes([emptyBox()])} data-testid="volcalc-reset"
                              className="rounded-xl border border-slate-300 px-4 text-navy hover:bg-slate-100"><RotateCcw className="h-4 w-4" /></button>
                    </div>
                  </div>
                </div>

                {/* Currency */}
                <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-lg" data-testid="curconv-card">
                  <div className="border-b border-slate-100 bg-emerald-700 p-5">
                    <h3 className="flex items-center gap-2 font-heading text-base font-bold text-white"><Coins className="h-5 w-5 text-emerald-200" /> Currency Converter</h3>
                    <p className="mt-1 text-[11px] text-emerald-200">Quickly convert international currency values while planning your shipment.</p>
                  </div>
                  <div className="p-5 sm:p-6">
                    <div><label className={label}>Amount</label>
                      <input type="number" min="0" className={input} data-testid="curconv-amount" value={cur.amount} onChange={(e) => setCur({ ...cur, amount: e.target.value })} /></div>
                    <div className="mt-3 grid grid-cols-[1fr_auto_1fr] items-end gap-2">
                      <div><label className={label}>From</label>
                        <select className={input} data-testid="curconv-from" value={cur.from} onChange={(e) => setCur({ ...cur, from: e.target.value })}>
                          {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select></div>
                      <button type="button" onClick={() => setCur({ ...cur, from: cur.to, to: cur.from })} data-testid="curconv-swap"
                              className="mb-2 grid h-9 w-9 place-items-center rounded-full border border-slate-200 text-slate-400 hover:border-saffron hover:text-saffron">
                        <ArrowLeftRight className="h-4 w-4" /></button>
                      <div><label className={label}>To</label>
                        <select className={input} data-testid="curconv-to" value={cur.to} onChange={(e) => setCur({ ...cur, to: e.target.value })}>
                          {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select></div>
                    </div>
                    <div className="mt-5 rounded-2xl bg-navy p-5 text-center">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">1 {cur.from} = {unitRate ? unitRate.toLocaleString("en-IN", { maximumFractionDigits: 2 }) : "…"} {cur.to}</p>
                      <p className="mt-1.5 font-heading text-3xl font-extrabold text-white" data-testid="curconv-result">
                        {converted === null ? "…" : `${(parseFloat(cur.amount) || 0).toLocaleString("en-IN")} ${cur.from} = ${converted.toLocaleString("en-IN", { maximumFractionDigits: 2 })} ${cur.to}`}
                      </p>
                      <p className="mt-1.5 text-[10px] text-slate-500" data-testid="curconv-updated">
                        {forex ? `Last updated: ${(forex.fetched_at || "").slice(0, 10)} · ${forex.source === "fallback-indicative" ? "indicative rates" : "live rates"}` : "Fetching rates…"}
                      </p>
                    </div>
                    <p className="mt-3 text-[10px] leading-relaxed text-slate-400">
                      Exchange rates are indicative and may differ from rates used by banks, payment providers or courier billing systems.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* JOURNEY */}
            <section className="py-12" data-testid="pickup-journey">
              <h2 className="text-center font-heading text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">From Pickup to Delivery</h2>
              <div className="relative mt-10 grid grid-cols-2 gap-6 sm:grid-cols-4 lg:grid-cols-7">
                <span className="absolute left-0 right-0 top-6 hidden h-0.5 bg-gradient-to-r from-saffron via-slate-200 to-emerald-600 lg:block" />
                {JOURNEY.map((j, i) => (
                  <div key={i} className="relative flex flex-col items-center text-center" data-testid={`pickup-journey-${i}`}>
                    <span className="relative z-10 grid h-12 w-12 place-items-center rounded-full bg-navy text-saffron ring-4 ring-slate-50"><j.icon className="h-5 w-5" /></span>
                    <p className="mt-2.5 text-xs font-bold text-navy">{j.title}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* WHY */}
            <section className="py-12" data-testid="pickup-why">
              <h2 className="text-center font-heading text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">Why Book With IndiaFast</h2>
              <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {[
                  [Package, "Free Pickup", "Doorstep collection where applicable."],
                  [Truck, "Multiple Courier Partners", "Access suitable courier options for every lane."],
                  [Search, "Easy Tracking", "Follow your shipment online at every step."],
                  [Globe2, "Domestic & International", "Ship within India or to 200+ countries."],
                  [Headphones, "Customer Support", "Get assistance whenever you need it."],
                  [PackageCheck, "Professional Shipping", "Technology-enabled courier operations."],
                ].map(([Icon, t, d], i) => (
                  <div key={i} className="group flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:-translate-y-1 hover:border-saffron/40 hover:shadow-lg" data-testid={`pickup-why-${i}`}>
                    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-navy text-saffron transition-colors group-hover:bg-saffron group-hover:text-white"><Icon className="h-5 w-5" /></span>
                    <div>
                      <p className="font-heading text-sm font-bold text-navy">{t}</p>
                      <p className="mt-0.5 text-xs text-slate-500">{d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* PACKAGING */}
            <section className="py-12" data-testid="pickup-packaging">
              <div className="overflow-hidden rounded-3xl bg-navy p-8 sm:p-10">
                <div className="tricolor-beam absolute inset-x-0 h-1" />
                <h2 className="font-heading text-2xl font-extrabold tracking-tight text-white sm:text-3xl">Pack It Right. Ship It With Confidence.</h2>
                <div className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-5">
                  {PACK_CARDS.map((p, i) => (
                    <div key={i} className="rounded-2xl border border-white/10 bg-white/5 p-4 text-center" data-testid={`pickup-pack-${i}`}>
                      <span className="mx-auto grid h-11 w-11 place-items-center rounded-xl bg-saffron/15 text-saffron"><p.icon className="h-5 w-5" /></span>
                      <p className="mt-2.5 text-xs font-bold text-white">{p.label}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-6 flex flex-wrap items-center gap-4">
                  <p className="text-sm font-semibold text-slate-300">Need Packing Help?</p>
                  <Link to="/media-videos" data-testid="pickup-packing-guide"
                        className="group flex items-center gap-1.5 rounded-full bg-saffron px-5 py-2.5 text-xs font-bold text-white shadow-lg shadow-saffron/30 hover:bg-saffron-hover">
                    View Packing Guide <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </section>
          </>
        )}

        {/* BAZAAR */}
        <section className="py-12" data-testid="pickup-bazaar">
          {hasShop ? (
            <ShopStrip />
          ) : (
            <div className="rounded-3xl border border-slate-200 bg-white p-12 text-center shadow-sm" data-testid="pickup-bazaar-empty">
              <ShoppingBag className="mx-auto h-10 w-10 text-saffron/50" />
              <h2 className="mt-4 font-heading text-xl font-extrabold text-navy">IndiaFast Bazaar is Coming Soon</h2>
              <p className="mt-1 text-sm text-slate-500">Discover products from India, delivered by IndiaFast.</p>
              <Link to="/shop" className="mt-5 inline-block rounded-full bg-saffron px-6 py-2.5 text-sm font-bold text-white shadow-lg shadow-saffron/30 hover:bg-saffron-hover">Explore Bazaar</Link>
            </div>
          )}
        </section>
      </main>

      {/* FINAL CTA */}
      {!done && (
        <section className="relative overflow-hidden bg-navy py-20 sm:py-24" data-testid="pickup-final-cta">
          <img src="https://images.pexels.com/photos/6682787/pexels-photo-6682787.jpeg?auto=compress&cs=tinysrgb&w=1920"
               alt="Customer receiving an IndiaFast delivery" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-navy/40" />
          <div className="tricolor-beam absolute inset-x-0 top-0 h-1" />
          <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
            <h2 className="font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">Ready to Send Your Shipment?</h2>
            <p className="mx-auto mt-4 max-w-xl text-base text-slate-300">Schedule your pickup with IndiaFast and let us handle the journey.</p>
            <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
              <button onClick={() => scrollTo(formRef)} data-testid="pickup-final-book"
                      className="rounded-full bg-saffron px-8 py-3.5 text-sm font-bold text-white shadow-xl shadow-saffron/30 transition-all hover:-translate-y-0.5 hover:bg-saffron-hover">Request Pickup</button>
              <button onClick={() => scrollTo(calcRef)} data-testid="pickup-final-calc"
                      className="rounded-full border border-white/25 bg-white/10 px-8 py-3.5 text-sm font-bold text-white backdrop-blur transition-colors hover:bg-white/20">Calculate Weight</button>
              <Link to="/track" data-testid="pickup-final-track"
                    className="rounded-full border border-white/25 px-8 py-3.5 text-sm font-bold text-white transition-colors hover:bg-white/10">Track Shipment</Link>
            </div>
          </div>
        </section>
      )}

      <Footer contact={contact} />
    </div>
  );
}
